import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Container,
  VStack,
  StackSeparator,
  Skeleton,
  Text,
  Box,
  Heading,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import QuickSearchForm from '@/components/ui/quick-search-form';
import WorkspaceShell from '@/components/layout/workspace-shell';
import CollectionDrawer from './collection-drawer';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import { Workspace } from '@/lib/types';
import { baseApi } from '@/utils/api';

interface Collection {
  id: number;
  name: string;
  description: string;
  reference_id: string;
  public_link: string;
  CreatedAt: string;
}

interface CollectionType {
  id: number;
  name: string;
  description: string;
  reference_id: string;
  workspace_id: number;
  workspace: Workspace;
}

export default function PublicCollectionList() {
  const [isLoading, setIsLoading] = useState(false);
  const [collectionType, setCollectionType] = useState<CollectionType>();
  const [isLoadingType, setIsLoadingType] = useState(false);
  const [allCollections, setAllCollections] = useState<Collection[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [refId, setRefId] = useState<string | null>(null);
  const itemDrawer = useModalToggle();
  const { referenceId } = useParams<{
    referenceId: string;
  }>();

  const getCollectionType = useCallback(async () => {
    if (!referenceId) {
      return;
    }

    setIsLoadingType(true);
    try {
      const response = await baseApi.get(`/public/collection-types/${referenceId}`);
      setCollectionType(response.data);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to load collection type',
      });
    } finally {
      setIsLoadingType(false);
    }
  }, [referenceId]);

  const fetchData = useCallback(async () => {
    if (!referenceId) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await baseApi.get(`/public/collections/collection-types/${referenceId}`);
      const fetchedCollections = response.data || [];
      setAllCollections(fetchedCollections);
      setCollections(fetchedCollections);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to load collections',
      });
    } finally {
      setIsLoading(false);
    }
  }, [referenceId]);

  useEffect(() => {
    getCollectionType();
    fetchData();
  }, [fetchData, getCollectionType]);

  const handleSearchChange = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);

      if (!searchValue.trim()) {
        setCollections(allCollections);
        return;
      }

      const filteredCollections = allCollections.filter(
        (collection) =>
          collection.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          (collection.description &&
            collection.description.toLowerCase().includes(searchValue.toLowerCase())),
      );

      setCollections(filteredCollections);
    },
    [allCollections],
  );

  const handleView = (item: Collection) => {
    setRefId(item.reference_id);
    itemDrawer.open();
  };

  return (
    <WorkspaceShell
      workspaceName={collectionType?.workspace?.name}
      workspaceLogo={collectionType?.workspace?.logo}
      workspaceBannerImage={collectionType?.workspace?.banner_image}
    >
      <Container maxW='5xl' px={{ base: 4, md: 6 }} mt={{ base: -6, md: -10 }} pb={10}>
        <Box bg='white' borderRadius='2xl' shadow={{ base: 'sm', md: 'lg' }} p={{ base: 5, md: 8 }}>
          {isLoadingType && (
            <VStack align='stretch' mb={5}>
              <Skeleton variant='shine' height={'40px'} />
            </VStack>
          )}

          {collectionType && !isLoadingType && (
            <Heading
              as='h1'
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight='bold'
              letterSpacing='-0.01em'
              mb={5}
            >
              {collectionType?.name || 'Collections'}
            </Heading>
          )}

          <Box mb={6} w={'full'}>
            <QuickSearchForm handleSearch={handleSearchChange} />
          </Box>

          {isLoading && (
            <VStack
              separator={<StackSeparator borderColor={'gray.100'} />}
              gap={2}
              align={'stretch'}
            >
              <Skeleton variant='shine' height={'40px'} />
              <Skeleton variant='shine' height={'40px'} />
              <Skeleton variant='shine' height={'40px'} />
              <Skeleton variant='shine' height={'40px'} />
              <Skeleton variant='shine' height={'40px'} />
              <Skeleton variant='shine' height={'40px'} />
            </VStack>
          )}

          {collections.length === 0 && !isLoading ? (
            <Text fontSize='sm' color='fg.muted' textAlign='center'>
              {searchTerm ? 'No matching collections found' : 'No collections available'}
            </Text>
          ) : (
            <VStack
              separator={<StackSeparator borderColor={'gray.100'} />}
              gap={2}
              align={'stretch'}
            >
              {collections.map((item) => (
                <Card.Root
                  key={item.id}
                  _hover={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleView(item);
                  }}
                >
                  <Card.Body p={2}>
                    <Card.Title fontSize={'sm'} lineHeight={1.5}>
                      {item.name}
                    </Card.Title>
                    <Card.Description mt={1} color={'fg.muted'} fontSize={'xs'}>
                      {new Date(item.CreatedAt).toLocaleDateString()}
                    </Card.Description>
                  </Card.Body>
                </Card.Root>
              ))}
            </VStack>
          )}

          {searchTerm && collections.length > 0 && (
            <Box mt={4} textAlign='center'>
              <Text fontSize='sm' color='gray.500'>
                Found {collections.length} {collections.length === 1 ? 'result' : 'results'} for "
                {searchTerm}"
              </Text>
            </Box>
          )}
        </Box>
      </Container>

      {refId && <CollectionDrawer {...itemDrawer.props} referenceId={refId} />}
    </WorkspaceShell>
  );
}
